import { useEffect, useMemo, useRef, useState } from 'react';
import {
  fetchMemberRecords,
  fetchWorkloadDetail,
  type LaneView,
  type WorkloadDetail,
} from '../../../api/work';
import './insights.css';

/**
 * Manpower distribution — open tickets per lane × member.
 *
 * Ported from the predecessor's Insights tab (`ManpowerHeatmap.tsx`), and the
 * reading it was built for is kept: **the outlined cell in each column is that
 * person's home lane**, tinted in the lane's colour; every other non-empty cell
 * is work *outside* it, in a warm gradient. A column that is warm away from its
 * outline is someone pulled across lanes, which a per-person total cannot show.
 *
 * **The numbers are `/api/work/workload/detail`'s**, the same ones the
 * Workload page's "open workload by lane" draws, so the two views cannot
 * disagree. Open means *not in* `work.metrics.workflow.status_sets.
 * engineer_terminal` — terminal statuses plus the holding states where a
 * ticket waits on someone outside the team. The heading prints that set.
 * Before extensions 0.40.0 "open" meant "no Jira resolution", and this
 * Jira's PROJ workflow closes to `Done` without setting one: the same view
 * would have shown ~1,100 open assignments where there were 60.
 *
 * Two departures from the predecessor, both because its constants were one
 * deployment's configuration:
 *
 * - **Lanes, labels and colours come from `view.lanes`** (`work.trends.lanes`),
 *   not a hard-coded four.
 * - **A member with no home lane gets a neutral tint**, not the predecessor's
 *   silent default of "core" — which coloured every one of their cells as
 *   drift from a lane they were never assigned.
 *
 * The home lane is `work.trends`' assignment, read by the detail route through
 * the same helper `/api/work/workload` uses.
 *
 * Clicking a cell lists the tickets behind it, fetched then — counts always,
 * evidence on demand, as on the Workload page.
 */

/** The predecessor's drift base, a warm orange. */
const DRIFT = { r: 217, g: 100, b: 58 };
/** For a member `work.trends` does not place: no lane colour to claim. */
const NEUTRAL = { r: 139, g: 143, b: 163 };

type Rgb = { r: number; g: number; b: number };

function rgbOf(hex: string): Rgb | null {
  const digits = /^#?([0-9a-fA-F]{6})$/.exec(hex)?.[1];
  if (digits === undefined) return null;
  const n = Number.parseInt(digits, 16);
  return { r: (n >> 16) & 0xff, g: (n >> 8) & 0xff, b: n & 0xff };
}

function rgba(c: Rgb, a: number): string {
  return `rgba(${c.r}, ${c.g}, ${c.b}, ${a.toFixed(3)})`;
}

interface Active {
  person: string;
  lane: string;
  count: number;
  anchor: { top: number; left: number };
}

interface Ticket {
  key: string;
  summary: string;
  status: string;
}

export function ManpowerHeatmap({ lanes }: { lanes: LaneView[] }) {
  const [data, setData] = useState<WorkloadDetail | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [active, setActive] = useState<Active | null>(null);
  const [tickets, setTickets] = useState<Ticket[] | null>(null);
  const [ticketsError, setTicketsError] = useState<string | null>(null);
  const popover = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    let live = true;
    fetchWorkloadDetail()
      .then((d) => live && setData(d))
      .catch((e: unknown) => live && setError(e instanceof Error ? e.message : String(e)));
    return () => {
      live = false;
    };
  }, []);

  // The cell's tickets, fetched when a reader asks for them.
  useEffect(() => {
    if (!active || !data) return;
    let live = true;
    setTickets(null);
    setTicketsError(null);
    fetchMemberRecords(active.person)
      .then((r) => {
        if (!live) return;
        const laneOf = data.project_lanes ?? {};
        setTickets(
          (r.records.open ?? [])
            .filter((t) => laneOf[String(t.project)] === active.lane)
            .map((t) => ({
              key: String(t.key ?? ''),
              summary: typeof t.summary === 'string' ? t.summary : '',
              status: typeof t.status === 'string' ? t.status : '',
            })),
        );
      })
      .catch((e: unknown) => live && setTicketsError(e instanceof Error ? e.message : String(e)));
    return () => {
      live = false;
    };
  }, [active, data]);

  // Close on an outside click or Escape. Attached a tick late so the click
  // that opened the popover does not immediately close it.
  useEffect(() => {
    if (!active) return;
    const onDown = (e: MouseEvent) => {
      if (popover.current && !popover.current.contains(e.target as Node)) setActive(null);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setActive(null);
    };
    const timer = setTimeout(() => {
      document.addEventListener('mousedown', onDown);
      document.addEventListener('keydown', onKey);
    }, 0);
    return () => {
      clearTimeout(timer);
      document.removeEventListener('mousedown', onDown);
      document.removeEventListener('keydown', onKey);
    };
  }, [active]);

  /** Rows: the configured lanes in their order, then any the data adds. */
  const rows = useMemo(() => {
    const out = lanes.map((l) => ({ category: l.category, label: l.label, color: l.color }));
    const known = new Set(out.map((l) => l.category));
    for (const lane of Object.values(data?.project_lanes ?? {})) {
      if (!known.has(lane)) {
        known.add(lane);
        out.push({ category: lane, label: lane, color: '#8b8fa3' });
      }
    }
    return out;
  }, [lanes, data]);

  const members = useMemo(
    () =>
      (data?.members ?? [])
        .filter((m) => m.counts !== null && m.open_by_lane)
        .map((m) => ({
          person: m.person,
          native: m.native_lane ?? null,
          byLane: m.open_by_lane ?? {},
          total: Object.values(m.open_by_lane ?? {}).reduce((a, n) => a + n, 0),
          unmapped: m.open_unmapped ?? 0,
        }))
        // The predecessor omitted anyone with nothing open; an all-empty
        // column says nothing a missing one does not.
        .filter((m) => m.total > 0),
    [data],
  );

  if (error) {
    return (
      <section className="lhc">
        <span className="nb__label">Manpower distribution</span>
        <p className="in-dim">{error}</p>
      </section>
    );
  }
  if (data === null) {
    return (
      <section className="lhc">
        <span className="nb__label">Manpower distribution</span>
        <p className="in-dim">Loading…</p>
      </section>
    );
  }
  if (members.length === 0) {
    return (
      <section className="lhc">
        <span className="nb__label">Manpower distribution</span>
        <p className="in-dim">No open tickets assigned to anyone on the roster.</p>
      </section>
    );
  }

  const laneMax: Record<string, number> = {};
  let driftMax = 0;
  for (const m of members) {
    for (const row of rows) {
      const n = m.byLane[row.category] ?? 0;
      laneMax[row.category] = Math.max(laneMax[row.category] ?? 0, n);
      if (m.native !== row.category) driftMax = Math.max(driftMax, n);
    }
  }
  const laneTotal = (lane: string) => members.reduce((a, m) => a + (m.byLane[lane] ?? 0), 0);
  const unmapped = members.reduce((a, m) => a + m.unmapped, 0);
  const failed = (data.members ?? []).filter((m) => m.counts === null).length;

  function background(lane: string, color: string, n: number, native: string | null): string {
    if (n <= 0) return 'transparent';
    if (native === null) {
      const norm = (laneMax[lane] ?? 0) > 0 ? n / (laneMax[lane] ?? 1) : 0;
      return rgba(NEUTRAL, 0.1 + norm * 0.35);
    }
    if (native === lane) {
      const norm = (laneMax[lane] ?? 0) > 0 ? n / (laneMax[lane] ?? 1) : 0;
      return rgba(rgbOf(color) ?? NEUTRAL, 0.1 + norm * 0.45);
    }
    const norm = driftMax > 0 ? Math.min(n / driftMax, 1) : 0;
    return rgba(DRIFT, 0.22 + norm * 0.55);
  }

  function open(e: React.SyntheticEvent<HTMLTableCellElement>, person: string, lane: string, count: number) {
    if (count <= 0) return;
    if (active?.person === person && active.lane === lane) {
      setActive(null);
      return;
    }
    const rect = e.currentTarget.getBoundingClientRect();
    setActive({ person, lane, count, anchor: { top: rect.bottom, left: rect.left } });
  }

  const activeLabel = rows.find((r) => r.category === active?.lane)?.label ?? active?.lane;

  return (
    <section className="lhc mph">
      <div className="nb__head">
        <span className="nb__label">Manpower distribution · open tickets per lane × member</span>
        <span className="in-dim">
          outlined = home lane · warm = work outside it · click a number for the tickets
        </span>
      </div>
      <p className="in-dim">
        Open means not in <code>{(data.open_excludes ?? []).join(', ') || '—'}</code>.
        {unmapped > 0 &&
          ` ${unmapped} open ticket${unmapped === 1 ? ' is' : 's are'} in projects no lane claims, and not shown.`}
        {failed > 0 && ` ${failed} member${failed === 1 ? '' : 's'} could not be read.`}
      </p>
      <div className="mph__scroll">
        <table className="mph__table">
          <thead>
            <tr>
              <th className="mph__lane-head">Lane</th>
              {members.map((m) => (
                <th key={m.person} title={m.native ? `${m.person} · home lane ${m.native}` : `${m.person} · no home lane`}>
                  {m.person}
                </th>
              ))}
              <th className="mph__sum">Σ</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((row) => (
              <tr key={row.category}>
                <td className="mph__lane" style={{ color: row.color }}>
                  {row.label}
                </td>
                {members.map((m) => {
                  const n = m.byLane[row.category] ?? 0;
                  const home = m.native === row.category;
                  const isActive = active?.person === m.person && active.lane === row.category;
                  return (
                    <td
                      key={m.person}
                      className={`mph__cell${home ? ' mph__cell--home' : ''}${isActive ? ' mph__cell--active' : ''}${n > 0 ? ' mph__cell--live' : ''}`}
                      style={{ background: background(row.category, row.color, n, m.native) }}
                      title={`${m.person} · ${row.label} · ${n} open${home ? ' (home lane)' : ''}`}
                      role={n > 0 ? 'button' : undefined}
                      tabIndex={n > 0 ? 0 : undefined}
                      onClick={(e) => open(e, m.person, row.category, n)}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter' || e.key === ' ') {
                          e.preventDefault();
                          open(e, m.person, row.category, n);
                        }
                      }}
                    >
                      {n > 0 ? n : '·'}
                    </td>
                  );
                })}
                <td className="mph__sum">{laneTotal(row.category)}</td>
              </tr>
            ))}
            <tr className="mph__totals">
              <td className="mph__lane-head">Σ</td>
              {members.map((m) => (
                <td key={m.person} className="mph__sum">
                  {m.total}
                </td>
              ))}
              <td className="mph__sum" />
            </tr>
          </tbody>
        </table>
      </div>

      {active && (
        <div
          ref={popover}
          className="mph__pop"
          role="dialog"
          aria-label={`${active.person} — ${activeLabel} tickets`}
          style={{ top: active.anchor.top + 4, left: active.anchor.left }}
        >
          <div className="mph__pop-head">
            <span>
              {active.person} · {activeLabel} · {active.count} open
            </span>
            <button type="button" onClick={() => setActive(null)} aria-label="Close">
              ×
            </button>
          </div>
          {ticketsError ? (
            <p className="in-dim mph__pop-body">{ticketsError}</p>
          ) : tickets === null ? (
            <p className="in-dim mph__pop-body">Loading…</p>
          ) : (
            <ul className="mph__pop-list">
              {tickets.map((t) => (
                <li key={t.key}>
                  <span className="mph__key">{t.key}</span>
                  <span className="mph__summary">{t.summary || <em>(no title)</em>}</span>
                  <span className="in-badge">{t.status}</span>
                </li>
              ))}
              {tickets.length < active.count && (
                <li className="in-dim">
                  {active.count - tickets.length} more not listed — the evidence list is capped at 200 per person.
                </li>
              )}
            </ul>
          )}
        </div>
      )}
    </section>
  );
}
